/**
 * Scene state — zustand store with built-in undo/redo.
 *
 * Operations log to a history stack (event-sourced per spec §10.1) so
 * the storyboard UI in MKO-113 can render thumbnails per Op.
 *
 * Single canvas for now. Multi-design support lands when sync ships
 * (Sprint 6 — MKO-119/MKO-120).
 */

import { create } from 'zustand';
import { SHAPES_BY_ID } from '../library/explorers-shapes.ts';
import { EXPLORERS_GRID_MM, type Op, type SceneObject, WORKPLANE_HALF_MM } from '../types.ts';

const HISTORY_DEPTH = 100;

const snapToGrid = (n: number, step = EXPLORERS_GRID_MM): number => Math.round(n / step) * step;

const clampToWorkplane = (v: number): number =>
  Math.max(-WORKPLANE_HALF_MM, Math.min(WORKPLANE_HALF_MM, v));

/**
 * Save indicator state machine. The full machine (per MKO-112 spec) also
 * has `synced` (post-verification cloud sync) and an offline variant —
 * those land once parent-account UI + sync exist. For now the only two
 * runtime states are saving/saved; `idle` covers "we haven't touched
 * IDB yet this session" so the pill can stay hidden until first edit.
 */
export type SaveStatus = 'idle' | 'saving' | 'saved';

interface SceneState {
  objects: Record<string, SceneObject>;
  /** Render order — keeps panel-add order stable. */
  order: string[];
  selectedId: string | null;

  history: Op[];
  redoStack: Op[];

  saveStatus: SaveStatus;

  /** Which design slot we're editing. The persist layer writes scene
   * changes to this id in IndexedDB. Switching to another design
   * loads its scene_json and updates this field. */
  currentDesignId: string;
  /** User-facing label for the current design. Editable via the
   * Designs panel. */
  currentDesignName: string;

  /** Add a shape from the library at the workplane center (snapped). */
  addShape: (shapeRefId: string) => string;

  /** Move a shape to a new position; snaps + clamps to workplane. */
  moveShape: (id: string, position: [number, number, number]) => void;

  /** Uniformly scale (Explorers tier — proportional only). */
  scaleShape: (id: string, scale: number) => void;

  /** Delete a shape. */
  deleteShape: (id: string) => void;

  /** Selection (used by ScaleHandle, DeleteZone). */
  select: (id: string | null) => void;

  /** Undo last op. */
  undo: () => void;
  /** Redo last undone op. */
  redo: () => void;

  /** Wipe everything (used by "reset challenge"). */
  reset: () => void;

  /** Used by the persistence layer (canvas/storage/persist.ts). */
  setSaveStatus: (status: SaveStatus) => void;
  /** Used by the persistence layer when switching designs / hydrating. */
  setCurrentDesign: (id: string, name: string) => void;
}

let nextId = 0;
const createId = (): string => `obj-${Date.now()}-${nextId++}`;

const pushOp = (state: SceneState, op: Op): Pick<SceneState, 'history' | 'redoStack'> => {
  const history = [...state.history, op].slice(-HISTORY_DEPTH);
  return { history, redoStack: [] };
};

export const useScene = create<SceneState>((set, get) => ({
  objects: {},
  order: [],
  selectedId: null,
  history: [],
  redoStack: [],
  saveStatus: 'idle',
  currentDesignId: 'default',
  currentDesignName: 'My design',

  addShape: (shapeRefId) => {
    const ref = SHAPES_BY_ID[shapeRefId];
    if (!ref) throw new Error(`unknown shapeRefId: ${shapeRefId}`);
    const id = createId();
    const obj: SceneObject = {
      id,
      shapeRefId,
      position: [0, ref.params.height / 2, 0],
      scale: 1,
      rotation: [0, 0, 0],
      isHole: false,
    };
    set((s) => ({
      objects: { ...s.objects, [id]: obj },
      order: [...s.order, id],
      selectedId: id,
      ...pushOp(s, { type: 'add', after: obj }),
    }));
    return id;
  },

  moveShape: (id, position) => {
    const before = get().objects[id];
    if (!before) return;
    const snapped: [number, number, number] = [
      clampToWorkplane(snapToGrid(position[0])),
      Math.max(0, snapToGrid(position[1])),
      clampToWorkplane(snapToGrid(position[2])),
    ];
    if (
      snapped[0] === before.position[0] &&
      snapped[1] === before.position[1] &&
      snapped[2] === before.position[2]
    ) {
      return; // no-op (drag settled to original cell)
    }
    set((s) => ({
      objects: { ...s.objects, [id]: { ...before, position: snapped } },
      ...pushOp(s, { type: 'move', id, before: before.position, after: snapped }),
    }));
  },

  scaleShape: (id, scale) => {
    const before = get().objects[id];
    if (!before) return;
    const clamped = Math.max(0.25, Math.min(4, scale));
    if (clamped === before.scale) return;
    set((s) => ({
      objects: { ...s.objects, [id]: { ...before, scale: clamped } },
      ...pushOp(s, { type: 'scale', id, before: before.scale, after: clamped }),
    }));
  },

  deleteShape: (id) => {
    const before = get().objects[id];
    if (!before) return;
    set((s) => {
      const { [id]: _removed, ...rest } = s.objects;
      return {
        objects: rest,
        order: s.order.filter((x) => x !== id),
        selectedId: s.selectedId === id ? null : s.selectedId,
        ...pushOp(s, { type: 'delete', before }),
      };
    });
  },

  select: (id) => set({ selectedId: id }),

  undo: () => {
    const { history, redoStack, objects, order } = get();
    const op = history[history.length - 1];
    if (!op) return;
    const newHistory = history.slice(0, -1);
    const newRedo = [...redoStack, op].slice(-HISTORY_DEPTH);

    switch (op.type) {
      case 'add': {
        const { [op.after.id]: _, ...rest } = objects;
        set({
          objects: rest,
          order: order.filter((x) => x !== op.after.id),
          selectedId: null,
          history: newHistory,
          redoStack: newRedo,
        });
        break;
      }
      case 'delete': {
        set({
          objects: { ...objects, [op.before.id]: op.before },
          order: [...order, op.before.id],
          selectedId: op.before.id,
          history: newHistory,
          redoStack: newRedo,
        });
        break;
      }
      case 'move': {
        const obj = objects[op.id];
        if (!obj) return;
        set({
          objects: { ...objects, [op.id]: { ...obj, position: op.before } },
          history: newHistory,
          redoStack: newRedo,
        });
        break;
      }
      case 'scale': {
        const obj = objects[op.id];
        if (!obj) return;
        set({
          objects: { ...objects, [op.id]: { ...obj, scale: op.before } },
          history: newHistory,
          redoStack: newRedo,
        });
        break;
      }
    }
  },

  redo: () => {
    const { redoStack, history, objects, order } = get();
    const op = redoStack[redoStack.length - 1];
    if (!op) return;
    const newRedo = redoStack.slice(0, -1);
    const newHistory = [...history, op].slice(-HISTORY_DEPTH);

    switch (op.type) {
      case 'add': {
        set({
          objects: { ...objects, [op.after.id]: op.after },
          order: [...order, op.after.id],
          selectedId: op.after.id,
          history: newHistory,
          redoStack: newRedo,
        });
        break;
      }
      case 'delete': {
        const { [op.before.id]: _, ...rest } = objects;
        set({
          objects: rest,
          order: order.filter((x) => x !== op.before.id),
          selectedId: null,
          history: newHistory,
          redoStack: newRedo,
        });
        break;
      }
      case 'move': {
        const obj = objects[op.id];
        if (!obj) return;
        set({
          objects: { ...objects, [op.id]: { ...obj, position: op.after } },
          history: newHistory,
          redoStack: newRedo,
        });
        break;
      }
      case 'scale': {
        const obj = objects[op.id];
        if (!obj) return;
        set({
          objects: { ...objects, [op.id]: { ...obj, scale: op.after } },
          history: newHistory,
          redoStack: newRedo,
        });
        break;
      }
    }
  },

  reset: () => {
    set({
      objects: {},
      order: [],
      selectedId: null,
      history: [],
      redoStack: [],
    });
  },

  setSaveStatus: (status) => set({ saveStatus: status }),
  setCurrentDesign: (id, name) => set({ currentDesignId: id, currentDesignName: name }),
}));
